import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminRoofMaterial,
  InsertRoofMaterialBody,
  UpdateRoofMaterialNewImageBody,
  UpdateRoofMaterialRemoveImageBody,
} from "./types/roofMaterials";

export const useRoofMaterialsApi = () => {
  const api = useApi();

  const getAdminRoofMaterial = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/roof_materials`;
      return useSWRMutation<AdminRoofMaterial, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminRoofMaterial>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminRoofMaterials = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/patrol/roof_materials", query);
      return useSWR<Paginated<AdminRoofMaterial>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminRoofMaterial>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminRoofMaterial = useCallback(
    async (body: InsertRoofMaterialBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/roof_materials`;
      const id = await api.post<{ id: string }, InsertRoofMaterialBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminRoofMaterial = useCallback(
    async (
      roofMaterialsd: string,
      body: UpdateRoofMaterialRemoveImageBody | UpdateRoofMaterialNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/patrol/roof_materials/${roofMaterialsd}`;
      (await api.put<UpdateRoofMaterialRemoveImageBody | UpdateRoofMaterialNewImageBody>(url, body),
        mutateCache(["/admin/settings/patrol/roof_materials"]));
    },
    [api],
  );

  const deleteAdminRoofMaterial = useCallback(
    async (roofMaterialsd: string): Promise<void> => {
      const url = `/admin/settings/patrol/roof_materials/${roofMaterialsd}`;
      await api.del(url);
      mutateCache([`/admin/settings/patrol/roof_materials`]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (roofMaterialsd: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/patrol/roof_materials/${roofMaterialsd}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  const updateAdminRoofMaterialVisibility = useCallback(
    async (roofMaterialId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/roof_materials/${roofMaterialId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/roof_materials"]);
    },
    [api],
  );

  const updateAdminRoofMaterialOrder = useCallback(
    async (roofMaterialId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/roof_materials/${roofMaterialId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/settings/patrol/roof_materials"]);
    },
    [api],
  );

  return {
    getAdminRoofMaterial,
    useAdminRoofMaterials,
    insertAdminRoofMaterial,
    updateAdminRoofMaterial,
    updateAdminRoofMaterialVisibility,
    updateAdminRoofMaterialOrder,
    deleteAdminRoofMaterial,
    generateUploadImageUrl,
  };
};
